/**
 * 备料台③「素材」行 —— 产品图 + 人物合并(2026-08-06 编排页重排 C3/C4)。
 *
 * ═══ 合并的理由 ═══════════════════════════════════════════════════════════════
 * 重排前这是两块:一张 4 行高的「产品图」卡(标题 + 两颗按钮 + 三行说明),
 * 和一个夹在两张大卡之间的孤儿「主角人物」标签 + 按钮(真机截图上看得很清楚:
 * 它既不是卡片也不在卡片里)。两者是同一件事 —— 给这条片的**料**,不是"设置" ——
 * 却长着两个完全不同的样子。现在压成文本框底下的一行:
 *     素材 [产品图] [从产品库选] [人物]                          0/5000
 *
 * ═══ ★合并**不许**丢能力(评审专门盯的一条)═══════════════════════════════════
 * `ProductImagePanel` 带着 T2A 实体核对:VLM 分类出「产品=图X」、role 可改可删、
 * 低置信琥珀高亮、图从哪来的来源说明。压成两颗按钮后这块没地方放 —— 那才是真漏洞。
 * 修法是**按状态给版面**,不是删:
 *   - 零图 = 只有几颗触发按钮(空态不为一段"有图之后才用得上"的指引付版面);
 *   - 传了图 = 核对条**原样就地展开**,一个控件都没少。
 * 两种形态共用 ProductImagePanel 同一份实现(它的 `compact` 只换头部 + 开插槽),
 * 不复制第二份判据 —— 核对条正是本次重排最不能丢的那块能力。
 */
import type { Dispatch, SetStateAction } from "react";

import type { CharacterPick } from "@/entities/character/ui/CharacterPickerDialog";
import type { VideoEntity } from "../../api/videoService";
import type { FormState } from "../../model/videoCreateTypes";
import { castSignals, shouldHintCast } from "../../model/castHint";
import { realPersonBlockReason } from "../../model/characterPick";
import { syncBriefWithEntities } from "../../model/productBrief";
import {
  addBriefProduct,
  briefProductsFromLegacy,
  projectBrief,
  removeBriefProduct,
  toggleProductPoint,
  type BriefProduct,
} from "../../model/multiProductBrief";
import { CharacterPanel } from "./CharacterPanel";
import { ProductImagePanel } from "./ProductImagePanel";

interface Props {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  onPickCharacter: (pick: CharacterPick) => void;
  onClearCharacter: () => void;
  disabled?: boolean;
  /** 文案字数计数(贴在行尾 —— 它本来就是上方文本框的附属信息,不值一整行)。 */
  counter?: string;
  /** SEEDANCE 真人出镜 P0(0914 D2):`?cast=real` 落地态,透传给 CharacterPanel。 */
  castReal?: boolean;
  onNavigateToRealCreate?: () => void;
  /** 1005:当前视频草稿 id,透传给 ProductImagePanel(识别图片账单挂草稿)。 */
  draftId?: number | null;
  /** 1013 V6:「从产品库开始」进页 → 产品库抽屉自动弹出(透传 ProductImagePanel)。 */
  autoOpenProductLibrary?: boolean;
  /** V6:抽屉已自动弹出(页面据此收掉一次性标记)。 */
  onProductLibraryAutoOpened?: () => void;
}

export function VideoMaterialRow({
  form,
  setForm,
  onPickCharacter,
  onClearCharacter,
  disabled,
  counter,
  castReal,
  onNavigateToRealCreate,
  draftId,
  autoOpenProductLibrary,
  onProductLibraryAutoOpened,
}: Props) {
  // 文案 × 选人物联动(2026-08-06 Q3):只提示、不动数据 —— 猜错的代价不对称,见 castHint 头注。
  const hintCast = shouldHintCast({ userInput: form.userInput, characterId: form.characterId });
  const hitWords = hintCast ? castSignals(form.userInput).slice(0, 2).join("、") : "";
  const realPersonWarning = realPersonBlockReason(form);

  /**
   * 这一页当前选了哪几个品 —— 新草稿读数组,老草稿(只有三个旧键)读回单元素数组。
   * 两条路合到一处,下面的渲染与回调就只有一份代码。
   */
  const briefProducts = briefProductsOf(form);

  return (
    <div
      data-video-material-row
      aria-disabled={disabled}
      className={disabled ? "pointer-events-none opacity-60" : undefined}
    >
      <ProductImagePanel
        compact
        draftId={draftId}
        autoOpenLibrary={autoOpenProductLibrary}
        onLibraryAutoOpened={onProductLibraryAutoOpened}
        entities={form.entities ?? []}
        onChange={(entities: VideoEntity[], productRefKey: string) =>
          setForm((s) => ({
            ...s,
            entities,
            productRefKey,
            // VID-BRIEF-1 对账:选中的产品实体被删 → 卖点/事实随之清,不留脱钩态
            ...(syncBriefWithEntities(s, entities) ?? {}),
          }))
        }
        // 多选 toggle:没选过就加(满 4 个不再加,见 multiProductBrief 头注),选过就去掉。
        // 每次都把三个旧键投影回去 —— api 读的还是它们。
        pickedProductCodes={briefProducts.map((p) => p.code)}
        onPickProduct={(node) =>
          setForm((s) => {
            const cur = briefProductsOf(s);
            const next = cur.some((p) => p.code === node.code)
              ? removeBriefProduct(cur, node.code)
              : addBriefProduct(cur, node);
            return applyBriefProducts(s, next);
          })
        }
        leading={<span className="shrink-0 text-2xs text-muted-foreground">素材</span>}
        trailing={
          <>
            <CharacterPanel
              compact
              form={form}
              disabled={disabled}
              onPick={onPickCharacter}
              onClear={onClearCharacter}
              castReal={castReal}
              onNavigateToRealCreate={onNavigateToRealCreate}
            />
            {counter && (
              <span className="ml-auto shrink-0 text-2xs text-muted-foreground">{counter}</span>
            )}
          </>
        }
      />
      {/**
       * 选品即带料(VID-BRIEF-1)—— 2026-08-19 扩成**多产品**。
       *
       * ★真源是 `form.briefProducts` 数组;老草稿只有三个旧键,`briefProductsFromLegacy`
       *   把它读回成单元素数组 —— 所以这一块对新老草稿是同一段代码。
       * ★每次改动都顺手把三个旧键投影回去(`projectBrief`):api 的 `VideoDraftFormUtil`
       *   读的仍是那三个键,所以 api / ai **一行都不用改**。
       */}
      {briefProducts.length > 0 && (
        <div data-testid="product-brief-row" className="mt-2 space-y-2">
          {briefProducts.map((prod) => (
            <div
              key={prod.code}
              data-testid={`brief-product-${prod.code}`}
              className="flex flex-wrap items-center gap-1.5"
            >
              <span className="shrink-0 text-2xs font-medium text-foreground">{prod.name}</span>
              {prod.allPoints.length > 0 ? (
                <>
                  <span className="shrink-0 text-2xs text-muted-foreground">
                    卖点(默认全带,点掉不要的):
                  </span>
                  {prod.allPoints.map((p) => {
                    const on = prod.points.includes(p);
                    return (
                      <button
                        key={p}
                        type="button"
                        aria-pressed={on}
                        aria-label={`卖点「${p}」${on ? "已带上" : "已点掉"}`}
                        onClick={() =>
                          setForm((s) =>
                            applyBriefProducts(
                              s,
                              toggleProductPoint(briefProductsOf(s), prod.code, p)
                            )
                          )
                        }
                        className={
                          on
                            ? "rounded-full border border-primary/50 bg-primary/10 px-2 py-0.5 text-2xs text-primary"
                            : "rounded-full border border-border/60 px-2 py-0.5 text-2xs text-muted-foreground line-through opacity-60 hover:opacity-90"
                        }
                      >
                        {p}
                      </button>
                    );
                  })}
                </>
              ) : (
                <span className="text-2xs text-muted-foreground">
                  在产品树里还没录卖点 —— 编排会带上产品名,卖点可去产品树补。
                </span>
              )}
              {/* VID-PICK-2:无图选品没有实体行可删 —— 每个品各自的「不带了」出口
                  (有图选品也能用它只清卖点、留图)。 */}
              <button
                type="button"
                aria-label={`不带「${prod.name}」了`}
                onClick={() =>
                  setForm((s) =>
                    applyBriefProducts(s, removeBriefProduct(briefProductsOf(s), prod.code))
                  )
                }
                className="ml-auto shrink-0 text-2xs text-muted-foreground underline underline-offset-2 hover:text-destructive"
              >
                不带了
              </button>
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <label className="flex w-fit cursor-pointer items-center gap-1.5 text-2xs text-muted-foreground">
              <input
                type="checkbox"
                checked={form.briefIncludeBrandFacts === true}
                onChange={(e) =>
                  setForm((s) => ({ ...s, briefIncludeBrandFacts: e.target.checked }))
                }
                className="h-3.5 w-3.5 accent-primary"
              />
              附品牌事实(把知识库里审核过的资料一并交给编排,只引用不改写)
            </label>
          </div>
          {/* ★多选时品牌事实锚不住:productId 在 ai 侧是 require_approved 的 strong 作用域锚,
              选了多个锚谁都是猜。与其偷偷取第一个,不如把规则直接说给用户听。 */}
          {briefProducts.length > 1 && (
            <p className="text-2xs text-muted-foreground">
              选了 {briefProducts.length} 个产品 —— 卖点全都会带上;
              <b className="text-foreground">品牌事实只在单选一个产品时才锚得住</b>。
            </p>
          )}
        </div>
      )}
      {hintCast && (
        <p className="mt-1.5 text-2xs text-muted-foreground">
          你的文案里提到「{hitWords}」—— 要不要从人物库挑一个锁住形象?
          <span className="ml-1 opacity-80">不挑也行,引擎会自己产一个主角。</span>
        </p>
      )}
      {/* 0813:选了真人 → 提交前就说清楚会失败(拦提交在 VideoCreatePage.onSubmit,
          文案同源于 realPersonBlockReason —— 两处各写一遍就会漂)。 */}
      {realPersonWarning && (
        <p
          data-testid="real-person-warning"
          className="mt-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-2xs text-destructive"
        >
          {realPersonWarning}
        </p>
      )}
    </div>
  );
}

/** 当前选中的品(新草稿走数组;老草稿从三个旧键读回)。 */
function briefProductsOf(form: FormState): BriefProduct[] {
  return form.briefProducts ?? briefProductsFromLegacy(form as unknown as Record<string, unknown>);
}

/**
 * 写回选中的品 —— **数组与三个旧键一起更新**。
 *
 * 旧键不是冗余:api 的 `VideoDraftFormUtil` 从 formJson 读的就是它们
 * (productId / productName / sellingPoints)。只更新数组的话,选了品但 brief 是空的,
 * 而页面上 chips 好端端显示着 —— 典型的「界面对了、发出去的没对」。
 */
function applyBriefProducts(form: FormState, list: BriefProduct[]): FormState {
  return { ...form, briefProducts: list, ...projectBrief(list) };
}
